export type TypeDescriptionOption = {
  type?: 'string' | 'integer' | 'boolean';
  description?: string;
  optionGroup?: string;
  examples?: Array<string | number | boolean>;
  default?: string | number | boolean;
};

type TypeDescriptionFamilyOption = {
  required: boolean;
};

export type TypeDescriptionCombinedOption = TypeDescriptionOption &
  TypeDescriptionFamilyOption;

type TypeDescriptionFamily = {
  DATA_SOURCE_OPTIONS: Record<string, TypeDescriptionFamilyOption>;
  VIRTUAL_GRAPH_OPTIONS: Record<string, TypeDescriptionFamilyOption>;
};

type TypeDescriptionOptionDefault = {
  default?: string | number | boolean;
  examples?: [string | number | boolean];
};

export type TypeDescriptionSource = {
  label: string;
  dataSourceFamily: string;
  vendorClientDownloadUrl?: string;
  vendorClientDocumentationUrl?: string;
  identifierQuoteCharacter: string;
  driverInstalled?: boolean;
  DATA_SOURCE_OPTIONS?: Record<string, TypeDescriptionOptionDefault>;
};

export type DataSourceTypeDescription = {
  DATA_SOURCE_OPTIONS: Record<string, TypeDescriptionOption>;
  VIRTUAL_GRAPH_OPTIONS: Record<string, TypeDescriptionOption>;
  DATA_SOURCE_FAMILIES: Record<string, TypeDescriptionFamily>;
  DATA_SOURCE_TYPES: Record<string, TypeDescriptionSource>;
};

export type DataSourceFamily =
  keyof DataSourceTypeDescription['DATA_SOURCE_FAMILIES'];

export const UNKNOWN_DATA_SOURCE_FAMILY: DataSourceFamily = 'UKNOWN';
export const JDBC_DATA_SOURCE_FAMILY: DataSourceFamily = 'JDBC';
export const SUPPORTED_DATA_SOURCE_FAMILIES: Record<DataSourceFamily, boolean> =
  {
    [JDBC_DATA_SOURCE_FAMILY]: true,
  };

export type DataSourceType =
  keyof DataSourceTypeDescription['DATA_SOURCE_TYPES'];

export const SAMPLE_MUSIC_DATA_SOURCE_TYPE: DataSourceType =
  'SAMPLE_DATA_MUSIC';
export const SAMPLE_MUSIC_DATA_SOURCE_TYPE_LABEL = 'Music Demo';
export const UNKNOWN_DATA_SOURCE_TYPE: DataSourceType = 'UNKNOWN';
export const UNKNOWN_DATA_SOURCE_TYPE_LABEL = 'Unknown Data Source Type';
export const GENERIC_SQL_DATA_SOURCE_TYPE: DataSourceType = 'DEFAULT';
export const DEFAULT_SQL_QUOTE_CHARACTER = '"';

export enum DataSourceOption {
  ENDPOINT = 'jdbc.url',
  USERNAME = 'jdbc.username',
  PASSWORD = 'jdbc.password',
}

export const STANDARD_JDBC_PROPERTY_KEYS = new Set(
  Object.values(DataSourceOption)
);
export const JDBC_DRIVER_KEY = 'jdbc.driver';

// Data source option (set on the platform side) that flags a connection as too
// expensive to preview (e.g. BigQuery, where LIMIT is applied after a full
// scan). When set, Designer skips the `SELECT * ... LIMIT` preview query and
// sources editable column headers from the Catalog DB instead.
export const SKIP_PREVIEW_DATA_SOURCE_OPTION = 'designer-skip-preview';

export const getPreviewsDisabled = (
  options: Record<string, unknown> | null | undefined
): boolean => {
  const value = options?.[SKIP_PREVIEW_DATA_SOURCE_OPTION];
  return value === 'true';
};

// This is the default value as of Stardog 8.0.0, in case the user connects to
// an instance before the data_source_type_description endpoint was added.
// Because more up-to-date definitions are retrieved from the platform, do not
// reference this directly; instead use the dataSources.typeDescription redux
// state

export const defaultTypeDescription: DataSourceTypeDescription = {
  DATA_SOURCE_OPTIONS: {
    'jdbc.url': {
      type: 'string',
      description: 'The URL of the JDBC connection',
    },
    'jdbc.username': {
      type: 'string',
      description: 'The username used to make the JDBC connection',
    },
    'jdbc.password': {
      type: 'string',
      description: 'The password used to make the JDBC connection',
    },
    'jdbc.driver': {
      type: 'string',
      description:
        'The driver class name used to make the JDBC connection. If omitted, driver will be taken from registered Service Providers.',
      optionGroup: 'advanced options',
    },
    'mongodb.uri': {
      type: 'string',
      description: 'The URI for a MongoDB connection',
    },
    'elasticsearch.rest.urls': {
      type: 'string',
      description:
        'Whitespace-delimited list of connection `scheme://host:port` values for Elasticsearch. Scheme defaults to `http`.',
      examples: ['https://server1:9200 http://server2:9200 server3:9200'],
    },
    'elasticsearch.indexes': {
      type: 'string',
      description:
        'Optional comma-delimited list of indexes to make visible to the data source. Defaults to all indexes.',
      examples: ['my_index1,other_index'],
    },
    'elasticsearch.username': {
      type: 'string',
    },
    'elasticsearch.password': {
      type: 'string',
    },
    'cassandra.contact.point': {
      type: 'string',
      description:
        'The address of the Cassandra node(s) that the driver uses to discover the cluster topology',
    },
    'cassandra.port': {
      default: 9042,
      type: 'integer',
      description: 'The port to use to connect to the Cassandra host.',
      examples: [42],
      optionGroup: 'advanced options',
    },
    'cassandra.keyspace': {
      type: 'string',
      description: 'The Cassandra keyspace to use for this session',
    },
    'cassandra.username': {
      type: 'string',
      description: 'The username for the Cassandra cluster',
    },
    'cassandra.password': {
      type: 'string',
      description: 'The password for the Cassandra cluster',
    },
    'sparql.url': {
      type: 'string',
      description:
        'SPARQL query endpoint/connection string with database specified',
      examples: ['http://myhost:26023/testdb/query'],
    },
    'sparql.username': {
      type: 'string',
      description: 'The username to access the SPARQL endpoint',
    },
    'sparql.password': {
      type: 'string',
      description: 'The password to access the SPARQL endpoint',
    },
    'sparql.graphname': {
      type: 'string',
      description:
        'The graph name on the SPARQL endpoint to be mapped as virtual graph',
    },
    'unique.key.sets': {
      type: 'string',
      description:
        'For data sources that do not express unique constraints in their metadata, either because unique constraints are not supported or because the data source did not include some or all of the valid constraints for reasons such as performance concerns, this property is used to define additional constraints manually. The property value is a comma-separated list of keys that define unique rows in a table. Each key is itself a comma-separated list of schema-qualified columns, enclosed in parentheses. For example, if table `APP.CUSTOMERS` has an `ID` column that serves as a primary key and a pair of columns, `FNAME` and `LNAME`, that together are a unique key, the value to express that is: `(APP.CUSTOMERS.ID),(APP.CUSTOMERS.FNAME,APP.CUSTOMERS.LNAME)`',
    },
    'sql.dialect': {
      type: 'string',
      description:
        'The format of the generated SQL, for use when using an unsupported JDBC driver',
      examples: ['MYSQL'],
      optionGroup: 'advanced options',
    },
    'sql.schemas': {
      type: 'string',
      description:
        'A comma-separated list of schemas to append to the schema search path',
    },
    'sql.default.schema': {
      type: 'string',
      description: 'Override the default schema for the connected user',
    },
    defaultAutoCommit: {
      optionGroup: 'connection pool options',
    },
    defaultReadOnly: {
      optionGroup: 'connection pool options',
    },
    defaultTransactionIsolation: {
      optionGroup: 'connection pool options',
    },
    defaultCatalog: {
      optionGroup: 'connection pool options',
    },
    driverClassName: {
      optionGroup: 'connection pool options',
    },
    maxActive: {
      optionGroup: 'connection pool options',
    },
    maxIdle: {
      optionGroup: 'connection pool options',
    },
    minIdle: {
      optionGroup: 'connection pool options',
    },
    initialSize: {
      optionGroup: 'connection pool options',
    },
    maxWait: {
      optionGroup: 'connection pool options',
    },
    testOnBorrow: {
      optionGroup: 'connection pool options',
    },
    testOnReturn: {
      optionGroup: 'connection pool options',
    },
    timeBetweenEvictionRunsMillis: {
      optionGroup: 'connection pool options',
    },
    numTestsPerEvictionRun: {
      optionGroup: 'connection pool options',
    },
    minEvictableIdleTimeMillis: {
      optionGroup: 'connection pool options',
    },
    testWhileIdle: {
      optionGroup: 'connection pool options',
    },
    testOnConnect: {
      optionGroup: 'connection pool options',
    },
    password: {
      optionGroup: 'connection pool options',
    },
    url: {
      optionGroup: 'connection pool options',
    },
    username: {
      optionGroup: 'connection pool options',
    },
    validationQuery: {
      optionGroup: 'connection pool options',
    },
    validationQueryTimeout: {
      optionGroup: 'connection pool options',
    },
    validatorClassName: {
      optionGroup: 'connection pool options',
    },
    validationInterval: {
      optionGroup: 'connection pool options',
    },
    accessToUnderlyingConnectionAllowed: {
      optionGroup: 'connection pool options',
    },
    removeAbandoned: {
      optionGroup: 'connection pool options',
    },
    removeAbandonedTimeout: {
      optionGroup: 'connection pool options',
    },
    logAbandoned: {
      optionGroup: 'connection pool options',
    },
    poolPreparedStatements: {
      optionGroup: 'connection pool options',
    },
    maxOpenPreparedStatements: {
      optionGroup: 'connection pool options',
    },
    connectionProperties: {
      optionGroup: 'connection pool options',
    },
    initSQL: {
      optionGroup: 'connection pool options',
    },
    jdbcInterceptors: {
      optionGroup: 'connection pool options',
    },
    jmxEnabled: {
      optionGroup: 'connection pool options',
    },
    fairQueue: {
      optionGroup: 'connection pool options',
    },
    useEquals: {
      optionGroup: 'connection pool options',
    },
    object_name: {
      optionGroup: 'connection pool options',
    },
    abandonWhenPercentageFull: {
      optionGroup: 'connection pool options',
    },
    maxAge: {
      optionGroup: 'connection pool options',
    },
    useLock: {
      optionGroup: 'connection pool options',
    },
    dataSource: {
      optionGroup: 'connection pool options',
    },
    dataSourceJNDI: {
      optionGroup: 'connection pool options',
    },
    suspectTimeout: {
      optionGroup: 'connection pool options',
    },
    alternateUsernameAllowed: {
      optionGroup: 'connection pool options',
    },
    commitOnReturn: {
      optionGroup: 'connection pool options',
    },
    rollbackOnReturn: {
      optionGroup: 'connection pool options',
    },
    useDisposableConnectionFacade: {
      optionGroup: 'connection pool options',
    },
    logValidationErrors: {
      optionGroup: 'connection pool options',
    },
    propagateInterruptState: {
      optionGroup: 'connection pool options',
    },
    ignoreExceptionOnPreLoad: {
      optionGroup: 'connection pool options',
    },
    useStatementFacade: {
      optionGroup: 'connection pool options',
    },
  },
  VIRTUAL_GRAPH_OPTIONS: {
    base: {
      type: 'string',
      description: 'Base IRI used to resolve relative IRIs from virtual graphs',
    },
    'jdbc.statement.fetch.size': {
      type: 'integer',
      description:
        'A hint as to the number of rows that should be fetched from the database at a time. Used to balance network traffic with memory use.',
      examples: [42],
      optionGroup: 'advanced options',
    },
    'mappings.syntax': {
      type: 'string',
      examples: ['R2RML', 'STARDOG', 'SMS2'],
      optionGroup: 'advanced options',
    },
    'mappings.format': {
      type: 'string',
      description: 'For R2RML, the RDF format of the mappings',
      optionGroup: 'advanced options',
    },
    'cassandra.allow.filtering': {
      default: false,
      type: 'boolean',
      description:
        'Whether to include the `ALLOW FILTERING` clause at the end of Cassandra CQL queries. Not recommended for production use.',
      optionGroup: 'advanced options',
    },
    'sparql.statsbasedoptimization': {
      default: true,
      type: 'boolean',
      description:
        'Whether to enable statistics-based optimization while accessing the SPARQL endpoint',
      optionGroup: 'advanced options',
    },
    'parser.sql.quoting': {
      type: 'string',
      description: 'DB quoting convention used by the parser',
      examples: ['ANSI'],
      optionGroup: 'advanced options',
    },
    'sql.functions': {
      type: 'string',
      description:
        'A comma-separated list of SQL function names to register with the parser',
    },
    'percent.encode': {
      default: true,
      type: 'boolean',
      description:
        'Should IRI template strings be percent-encoded to be valid IRIs?',
      optionGroup: 'advanced options',
    },
    'default.mapping.include.tables': {
      type: 'string',
      description:
        'A comma-separated list of tables to include when generating default mappings. Incompatible with `default.mapping.exclude.tables`',
    },
    'default.mapping.exclude.tables': {
      type: 'string',
      description:
        'A comma-separated list of tables to exclude when generating default mappings. Incompatible with `default.mapping.include.tables`',
    },
    'schema.in.generated.mappings': {
      type: 'boolean',
      description:
        'Whether to include the name of the schema (along with the table name) in the templates for IRIs when automatically generating mappings',
      optionGroup: 'advanced options',
    },
    'union.strategy': {
      type: 'string',
      description:
        'Strategy for translating SPARQL UNION to SQL. For use when using an unsupported JDBC driver.',
      examples: ['COALESCED', 'EMBEDDED'],
      optionGroup: 'advanced options',
    },
    'charset.handling': {
      type: 'boolean',
      description:
        'Whether to prefix SQL NVARCHAR string literals with N. The default is dialect-dependent. For use when using an unsupported JDBC driver.',
      optionGroup: 'advanced options',
    },
  },
  DATA_SOURCE_FAMILIES: {
    CASSANDRA: {
      DATA_SOURCE_OPTIONS: {
        'cassandra.contact.point': {
          required: true,
        },
        'cassandra.port': {
          required: false,
        },
        'cassandra.keyspace': {
          required: true,
        },
        'cassandra.username': {
          required: true,
        },
        'cassandra.password': {
          required: true,
        },
      },
      VIRTUAL_GRAPH_OPTIONS: {
        base: {
          required: false,
        },
        'mappings.syntax': {
          required: false,
        },
        'mappings.format': {
          required: false,
        },
        'default.mapping.include.tables': {
          required: false,
        },
        'default.mapping.exclude.tables': {
          required: false,
        },
        'schema.in.generated.mappings': {
          required: false,
        },
        'percent.encode': {
          required: false,
        },
        'cassandra.allow.filtering': {
          required: false,
        },
      },
    },
    ELASTICSEARCH: {
      DATA_SOURCE_OPTIONS: {
        'elasticsearch.rest.urls': {
          required: true,
        },
        'elasticsearch.indexes': {
          required: false,
        },
        'elasticsearch.username': {
          required: false,
        },
        'elasticsearch.password': {
          required: false,
        },
      },
      VIRTUAL_GRAPH_OPTIONS: {
        base: {
          required: false,
        },
        'mappings.syntax': {
          required: false,
        },
        'mappings.format': {
          required: false,
        },
        'default.mapping.include.tables': {
          required: false,
        },
        'default.mapping.exclude.tables': {
          required: false,
        },
        'schema.in.generated.mappings': {
          required: false,
        },
        'percent.encode': {
          required: false,
        },
      },
    },
    JDBC: {
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          required: true,
        },
        'jdbc.username': {
          required: false,
        },
        'jdbc.password': {
          required: false,
        },
        'jdbc.driver': {
          required: false,
        },
        'sql.default.schema': {
          required: false,
        },
        'sql.schemas': {
          required: false,
        },
        'sql.dialect': {
          required: false,
        },
        defaultAutoCommit: {
          required: false,
        },
        defaultReadOnly: {
          required: false,
        },
        defaultTransactionIsolation: {
          required: false,
        },
        defaultCatalog: {
          required: false,
        },
        driverClassName: {
          required: false,
        },
        maxActive: {
          required: false,
        },
        maxIdle: {
          required: false,
        },
        minIdle: {
          required: false,
        },
        initialSize: {
          required: false,
        },
        maxWait: {
          required: false,
        },
        testOnBorrow: {
          required: false,
        },
        testOnReturn: {
          required: false,
        },
        timeBetweenEvictionRunsMillis: {
          required: false,
        },
        numTestsPerEvictionRun: {
          required: false,
        },
        minEvictableIdleTimeMillis: {
          required: false,
        },
        testWhileIdle: {
          required: false,
        },
        testOnConnect: {
          required: false,
        },
        password: {
          required: false,
        },
        url: {
          required: false,
        },
        username: {
          required: false,
        },
        validationQuery: {
          required: false,
        },
        validationQueryTimeout: {
          required: false,
        },
        validatorClassName: {
          required: false,
        },
        validationInterval: {
          required: false,
        },
        accessToUnderlyingConnectionAllowed: {
          required: false,
        },
        removeAbandoned: {
          required: false,
        },
        removeAbandonedTimeout: {
          required: false,
        },
        logAbandoned: {
          required: false,
        },
        poolPreparedStatements: {
          required: false,
        },
        maxOpenPreparedStatements: {
          required: false,
        },
        connectionProperties: {
          required: false,
        },
        initSQL: {
          required: false,
        },
        jdbcInterceptors: {
          required: false,
        },
        jmxEnabled: {
          required: false,
        },
        fairQueue: {
          required: false,
        },
        useEquals: {
          required: false,
        },
        object_name: {
          required: false,
        },
        abandonWhenPercentageFull: {
          required: false,
        },
        maxAge: {
          required: false,
        },
        useLock: {
          required: false,
        },
        dataSource: {
          required: false,
        },
        dataSourceJNDI: {
          required: false,
        },
        suspectTimeout: {
          required: false,
        },
        alternateUsernameAllowed: {
          required: false,
        },
        commitOnReturn: {
          required: false,
        },
        rollbackOnReturn: {
          required: false,
        },
        useDisposableConnectionFacade: {
          required: false,
        },
        logValidationErrors: {
          required: false,
        },
        propagateInterruptState: {
          required: false,
        },
        ignoreExceptionOnPreLoad: {
          required: false,
        },
        useStatementFacade: {
          required: false,
        },
      },
      VIRTUAL_GRAPH_OPTIONS: {
        base: {
          required: false,
        },
        'mappings.syntax': {
          required: false,
        },
        'mappings.format': {
          required: false,
        },
        'default.mapping.include.tables': {
          required: false,
        },
        'default.mapping.exclude.tables': {
          required: false,
        },
        'schema.in.generated.mappings': {
          required: false,
        },
        'percent.encode': {
          required: false,
        },
        'jdbc.statement.fetch.size': {
          required: false,
        },
        'charset.handling': {
          required: false,
        },
        'union.strategy': {
          required: false,
        },
        'parser.sql.quoting': {
          required: false,
        },
        'sql.functions': {
          required: false,
        },
      },
    },
    MONGODB: {
      DATA_SOURCE_OPTIONS: {
        'mongodb.uri': {
          required: true,
        },
      },
      VIRTUAL_GRAPH_OPTIONS: {
        base: {
          required: false,
        },
        'mappings.syntax': {
          required: false,
        },
        'mappings.format': {
          required: false,
        },
        'default.mapping.include.tables': {
          required: false,
        },
        'default.mapping.exclude.tables': {
          required: false,
        },
        'schema.in.generated.mappings': {
          required: false,
        },
        'percent.encode': {
          required: false,
        },
      },
    },
    SPARQL: {
      DATA_SOURCE_OPTIONS: {
        'sparql.url': {
          required: true,
        },
        'sparql.graphname': {
          required: true,
        },
        'sparql.username': {
          required: false,
        },
        'sparql.password': {
          required: false,
        },
      },
      VIRTUAL_GRAPH_OPTIONS: {
        'sparql.statsbasedoptimization': {
          required: false,
        },
      },
    },
  },
  DATA_SOURCE_TYPES: {
    CASSANDRA: {
      label: 'Apache Cassandra',
      dataSourceFamily: 'CASSANDRA',
      vendorClientDownloadUrl:
        'https://docs.datastax.com/en/developer/java-driver/latest/',
      vendorClientDocumentationUrl:
        'https://docs.datastax.com/en/developer/java-driver/latest/manual/core/',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
    },
    ELASTICSEARCH: {
      label: 'Elasticsearch',
      dataSourceFamily: 'ELASTICSEARCH',
      vendorClientDownloadUrl: 'https://www.elastic.co/downloads/elasticsearch',
      vendorClientDocumentationUrl:
        'https://www.elastic.co/guide/en/elasticsearch/client/java-rest/current/index.html',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
    },
    MONGODB: {
      label: 'MongoDB and Azure Cosmos DB',
      dataSourceFamily: 'MONGODB',
      vendorClientDownloadUrl:
        'http://mongodb.github.io/mongo-java-driver/4.0/driver/getting-started/installation/',
      vendorClientDocumentationUrl: 'https://docs.mongodb.com/drivers/java',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
    },
    SPARQL: {
      label: 'Stardog and SPARQL service endpoints',
      dataSourceFamily: 'SPARQL',
      vendorClientDocumentationUrl:
        'https://docs.stardog.com/virtual-graphs/data-sources/specific-data-source-considerations#stardogsparql-virtual-graph-considerations',
      identifierQuoteCharacter: '',
      driverInstalled: true,
    },
    ATHENA: {
      label: 'Amazon Athena',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://docs.aws.amazon.com/athena/latest/ug/connect-with-jdbc.html',
      vendorClientDocumentationUrl:
        'https://s3.amazonaws.com/athena-downloads/drivers/JDBC/SimbaAthenaJDBC_2.0.9/docs/Simba+Athena+JDBC+Driver+Install+and+Configuration+Guide.pdf',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:awsathena://<AWS hostname>:443;S3OutputLocation=s3://<bucket name>/<bucket path>;Schema=<schema name>',
          ],
        },
        'jdbc.driver': {
          default: 'com.simba.athena.jdbc.Driver',
        },
      },
    },
    BIGQUERY: {
      label: 'Google BigQuery',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://cloud.google.com/bigquery/providers/simba-drivers',
      vendorClientDocumentationUrl:
        'https://www.simba.com/products/BigQuery/doc/JDBC_InstallGuide/content/jdbc/bq/using/connectionurl.htm',
      identifierQuoteCharacter: '`',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:bigquery://https://bigquery.googleapis.com/bigquery/v2;ProjectId=<project>;DefaultDataset=<database>;OAuthType=0;OAuthServiceAcctEmail=<email>;OAuthPvtKeyPath=<private_key_path>;Timeout=60',
          ],
        },
        'jdbc.driver': {
          default: 'com.simba.googlebigquery.jdbc42.Driver',
        },
      },
    },
    DB2: {
      label: 'IBM DB2',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.ibm.com/support/pages/db2-jdbc-driver-versions-and-downloads',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:db2://<hostname>:50000/<database>'],
        },
        'jdbc.driver': {
          default: 'com.ibm.db2.jcc.DB2Driver',
        },
      },
    },
    DEFAULT: {
      label: 'Generic (ANSI SQL)',
      dataSourceFamily: 'JDBC',
      identifierQuoteCharacter: '"',
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:<driver>://<hostname>/<db>'],
        },
      },
    },
    DEFAULT_CDATA: {
      label: 'Cdata Default (MSSQL Style SQL)',
      dataSourceFamily: 'JDBC',
      identifierQuoteCharacter: '[',
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:<driver>:<options>'],
        },
      },
    },
    DERBY: {
      label: 'Apache Derby',
      dataSourceFamily: 'JDBC',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.driver': {
          default: 'org.apache.derby.jdbc.EmbeddedDriver',
        },
      },
    },
    EXASOL: {
      label: 'Exasol',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.exasol.com/portal/display/DOWNLOAD/Exasol+Download+Section',
      vendorClientDocumentationUrl:
        'https://docs.exasol.com/connect_exasol/drivers/jdbc.htm',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:exa:<hostname>:8563;schema=<database>'],
        },
        'jdbc.driver': {
          default: 'com.exasol.jdbc.EXADriver',
        },
      },
    },
    GOOGLESHEETS: {
      label: 'Google Sheets',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.cdata.com/drivers/gsheets/download/jdbc/',
      vendorClientDocumentationUrl:
        'https://cdn.cdata.com/help/RLG/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:googlesheets:InitiateOAuth=<method>;Spreadsheet=<sheetname>',
          ],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.googlesheets.GoogleSheetsDriver',
        },
      },
    },
    H2: {
      label: 'H2',
      dataSourceFamily: 'JDBC',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.driver': {
          default: 'org.h2.Driver',
        },
      },
    },
    HANA: {
      label: 'SAP HANA',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://developers.sap.com/tutorials/hxe-ua-install-hdb-client-linux.html',
      vendorClientDocumentationUrl:
        'https://help.sap.com/viewer/0eec0d68141541d1b07893a39944924e/2.0.02/en-US/ff15928cf5594d78b841fbbe649f04b4.html',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:sap://<hostname>/?databaseName=<database>&instanceNumber=<instance number>&currentSchema=<schema>',
          ],
        },
        'jdbc.driver': {
          default: 'com.sap.db.jdbc.Driver',
        },
      },
    },
    HIVE: {
      label: 'Apache Hive',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.cloudera.com/downloads/connectors/hive/jdbc/2-6-5.html',
      vendorClientDocumentationUrl:
        'https://docs.cloudera.com/HDPDocuments/HDP2/HDP-2.5.3/bk_data-access/content/hive-jdbc-odbc-drivers.html',
      identifierQuoteCharacter: '`',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:hive2://<hostname>:10000'],
        },
        'jdbc.driver': {
          default: 'org.apache.hive.jdbc.HiveDriver',
        },
      },
    },
    IMPALA: {
      label: 'Apache Impala',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.cloudera.com/downloads/connectors/impala/jdbc/2-6-15.html',
      vendorClientDocumentationUrl:
        'https://docs.cloudera.com/documentation/enterprise/latest/topics/impala_jdbc.html',
      identifierQuoteCharacter: '`',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:impala://<hostname>:21050'],
        },
        'jdbc.driver': {
          default: 'com.cloudera.impala.jdbc41.Driver',
        },
      },
    },
    JIRA: {
      label: 'Atlassian Jira',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://cdata.com/drivers/jira/download',
      vendorClientDocumentationUrl:
        'https://cdn.cdata.com/help/BJF/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:jira:User=<username>;APIToken=<token>;Url=https://<yoursite.atlassian.com>',
          ],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.jira.JIRADriver',
        },
      },
    },
    LDAP_CDATA: {
      label: 'LDAP (via CData JDBC)',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://www.cdata.com/drivers/ldap/download/',
      vendorClientDocumentationUrl:
        'http://cdn.cdata.com/help/RFF/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:ldap:User=<username>;Password=<password>;SecurityToken=<token>',
          ],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.ldap.LDAPDriver',
        },
      },
    },
    MSSQL: {
      label: 'Microsoft SQL Server',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://docs.microsoft.com/en-us/sql/connect/jdbc/download-microsoft-jdbc-driver-for-sql-server?view=sql-server-ver15',
      vendorClientDocumentationUrl:
        'https://docs.microsoft.com/en-us/sql/connect/jdbc/step-3-proof-of-concept-connecting-to-sql-using-java?view=sql-server-ver15',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:sqlserver://<hostname>:1433;databaseName=<database>',
          ],
        },
        'jdbc.driver': {
          default: 'com.microsoft.sqlserver.jdbc.SQLServerDriver',
        },
      },
    },
    MYSQL: {
      label: 'MySQL and MariaDB',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://dev.mysql.com/downloads/connector/j/',
      vendorClientDocumentationUrl:
        'https://dev.mysql.com/doc/connector-j/8.0/en/connector-j-usagenotes-connect-drivermanager.html',
      identifierQuoteCharacter: '`',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:mysql://<hostname>:3306/<database>'],
        },
        'jdbc.driver': {
          default: 'com.mysql.jdbc.Driver',
        },
      },
    },
    ORACLE: {
      label: 'Oracle',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html',
      vendorClientDocumentationUrl:
        'https://docs.oracle.com/cd/B28359_01/java.111/b31224/jdbcthin.htm',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:oracle:thin:@//<hostname>:1521/<database>'],
        },
        'jdbc.driver': {
          default: 'oracle.jdbc.driver.OracleDriver',
        },
      },
    },
    POSTGRESQL: {
      label: 'PostgreSQL',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://jdbc.postgresql.org/download.html',
      vendorClientDocumentationUrl:
        'https://jdbc.postgresql.org/documentation/head/connect.html',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:postgresql://<hostname>:5432/<database>'],
        },
        'jdbc.driver': {
          default: 'org.postgresql.Driver',
        },
      },
    },
    REDSHIFT: {
      label: 'Amazon Redshift',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://docs.aws.amazon.com/redshift/latest/mgmt/configure-jdbc-connection.html#download-jdbc-driver',
      vendorClientDocumentationUrl:
        'https://docs.aws.amazon.com/redshift/latest/mgmt/configure-jdbc-connection.html#obtain-jdbc-url',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:redshift://<hostname>:5439/<database>?ssl=true'],
        },
        'jdbc.driver': {
          default: 'com.amazon.redshift.jdbc.Driver',
        },
      },
    },
    REST: {
      label: 'REST API',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://cdata.com/drivers/rest/download',
      vendorClientDocumentationUrl:
        'https://cdn.cdata.com/help/DWF/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:rest:DataModel=<model>;URI=<Uri>;Format=<format>'],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.rest.RESTDriver',
        },
      },
    },
    SALESFORCE_SIMBA: {
      label: 'Salesforce (via Simba JDBC)',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.simba.com/drivers/salesforce-odbc-jdbc/#download',
      vendorClientDocumentationUrl:
        'https://www.simba.com/products/Salesforce/doc/JDBC_InstallGuide/content/jdbc/sf/using/connectionurl.htm',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:salesforce://<instance hostname>;Endpoint=https://<instance hostname>/services/Soap/u/46.0',
          ],
        },
        'jdbc.driver': {
          default: 'com.simba.salesforce.jdbc42.Driver',
        },
      },
    },
    SALESFORCE_CDATA: {
      label: 'Salesforce (via CData JDBC)',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://www.cdata.com/drivers/salesforce/download/',
      vendorClientDocumentationUrl:
        'http://cdn.cdata.com/help/RFF/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:salesforce:User=<username>;Password=<password>;SecurityToken=<token>',
          ],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.salesforce.SalesforceDriver',
        },
      },
    },
    SNOWFLAKE: {
      label: 'Snowflake',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://docs.snowflake.com/en/user-guide/jdbc-download.html#downloading-the-driver',
      vendorClientDocumentationUrl:
        'https://docs.snowflake.com/en/user-guide/jdbc.html',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:snowflake://<account_identifier>.snowflakecomputing.com/?db=<database_name>',
          ],
        },
        'jdbc.driver': {
          default: 'net.snowflake.client.jdbc.SnowflakeDriver',
        },
      },
    },
    SPARKSQL: {
      label: 'Databricks and Spark SQL',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://www.simba.com/drivers/spark-jdc-odbc',
      vendorClientDocumentationUrl:
        'https://www.simba.com/drivers/spark-jdc-odbc',
      identifierQuoteCharacter: '`',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:spark://<instance hostname>:10000/<database>'],
        },
        'jdbc.driver': {
          default: 'com.simba.spark.jdbc.Driver',
        },
      },
    },
    SPLUNK: {
      label: 'Splunk',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl: 'https://cdata.com/drivers/splunk/download',
      vendorClientDocumentationUrl:
        'https://cdn.cdata.com/help/FUF/jdbc/default.htm',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:splunk:user=<username>;password=<password>;Url=<Your Url>',
          ],
        },
        'jdbc.driver': {
          default: 'cdata.jdbc.splunk.SplunkDriver',
        },
      },
    },
    SYBASE: {
      label: 'Sybase Adaptive Server Enterprise (ASE)',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'http://infocenter.sybase.com/help/index.jsp?topic=/com.sybase.infocenter.dc32179.1600/doc/html/adh1344926096505.html',
      vendorClientDocumentationUrl:
        'http://infocenter.sybase.com/help/index.jsp?topic=/com.sybase.infocenter.dc39001.1600/doc/html/har1355975812097.html',
      identifierQuoteCharacter: '[',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: ['jdbc:sybase:Tds:<hostname>:5000'],
        },
        'jdbc.driver': {
          default: 'com.sybase.jdbc4.jdbc.SybDriver',
        },
      },
    },
    TERADATA: {
      label: 'Teradata',
      dataSourceFamily: 'JDBC',
      vendorClientDownloadUrl:
        'https://downloads.teradata.com/download/connectivity/jdbc-driver',
      vendorClientDocumentationUrl:
        'https://teradata-docs.s3.amazonaws.com/doc/connectivity/jdbc/reference/current/jdbcug_chapter_2.html#BABJIHBJ',
      identifierQuoteCharacter: '"',
      driverInstalled: true,
      DATA_SOURCE_OPTIONS: {
        'jdbc.url': {
          examples: [
            'jdbc:teradata://<hostname>/TMODE=ANSI,CHARSET=UTF16,DATABASE=<database>',
          ],
        },
        'jdbc.driver': {
          default: 'com.teradata.jdbc.TeraDriver',
        },
      },
    },
  },
};
